import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { RootRedirect } from "@/components/RootRedirect";

// Pages — existentes
import UploadPage from "./pages/UploadPage";
import DashboardLocalPage from "./pages/DashboardLocalPage";
import DashboardPage from "./pages/DashboardPage";
import NotasPage from "./pages/NotasPage";
import InspecaoPage from "./pages/InspecaoPage";
import RelatorioPage from "./pages/RelatorioPage";
import AdminPage from "./pages/AdminPage";
import NotFound from "./pages/NotFound";

// Pages — novas
import ProdutosPage from "./pages/ProdutosPage";
import FornecedoresPage from "./pages/FornecedoresPage";
import ClientesPage from "./pages/ClientesPage";
import FinanceiroPage from "./pages/FinanceiroPage";
import CfopPage from "./pages/CfopPage";
import TributacaoPage from "./pages/TributacaoPage";
import ClassificacaoFiscalPage from "./pages/ClassificacaoFiscalPage";
import ValidacaoXmlPage from "./pages/ValidacaoXmlPage";
import ExportacoesPage from "./pages/ExportacoesPage";
import InsightsPage from "./pages/InsightsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import AlertasPage from "./pages/AlertasPage";
import ParametrosPage from "./pages/ParametrosPage";
import RegrasPage from "./pages/RegrasPage";
import UsuariosPage from "./pages/UsuariosPage";
import PlanosPage from "./pages/PlanosPage";
import AssinaturasAdminPage from "./pages/AssinaturasAdminPage";
import MinhaAssinaturaPage from "./pages/MinhaAssinaturaPage";
import DivergenciasPage from "./pages/DivergenciasPage";
import ImportacoesPage from "./pages/ImportacoesPage";
import AjudaPage from "./pages/AjudaPage";
import TransportePage from "./pages/TransportePage";

// Auth & Legal
import LoginPage from "./pages/auth/LoginPage";
import MinimalLoginPage from "./pages/auth/MinimalLoginPage";
import SignupPage from "./pages/auth/SignupPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import AuthCallbackPage from "./pages/auth/AuthCallbackPage";
import PrivacidadePage from "./pages/legal/PrivacidadePage";
import TermosPage from "./pages/legal/TermosPage";
import SobrePage from "./pages/legal/SobrePage";
import AtualizacoesPage from "./pages/legal/AtualizacoesPage";
import CookiesPage from "./pages/legal/CookiesPage";
import DPOPage from "./pages/legal/DPOPage";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";

const queryClient = new QueryClient();

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

const App = () => (
  <AppErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
    <ThemeProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <Routes>
            {/* Auth */}
            <Route path="/login" element={<MinimalLoginPage />} />
            <Route path="/minimal-login" element={<MinimalLoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />

            {/* Legal */}
            <Route path="/privacidade" element={<PrivacidadePage />} />
            <Route path="/termos" element={<TermosPage />} />
            <Route path="/sobre" element={<SobrePage />} />
            <Route path="/atualizacoes" element={<AtualizacoesPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/contato-dpo" element={<DPOPage />} />
            {/* <Route path="/planos" element={<PlanosPage />} /> */}

            {/* App protegido */}
            <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
              <Route path="/" element={<RootRedirect />} />

              {/* OPERAÇÃO */}
              <Route path="/upload" element={<UploadPage />} />

              {/* VISÃO GERAL */}
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/dashboard-local" element={<DashboardLocalPage />} />
              <Route path="/insights" element={<InsightsPage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />

              {/* DATA ANALYSIS */}

              <Route path="/notas" element={<NotasPage />} />
              <Route path="/fornecedores" element={<FornecedoresPage />} />
              <Route path="/clientes" element={<ClientesPage />} />
              <Route path="/financeiro" element={<FinanceiroPage />} />
              <Route path="/transporte" element={<TransportePage />} />
              <Route path="/produtos" element={<ProdutosPage />} />
              <Route path="/tributacao" element={<TributacaoPage />} />
              <Route path="/cfop" element={<CfopPage />} />
              <Route path="/classificacao" element={<ClassificacaoFiscalPage />} />

              {/* SANITY CHECK & PROTOCOLS */}

              <Route path="/inspecao" element={<InspecaoPage />} />
              <Route path="/validacao-xml" element={<ValidacaoXmlPage />} />

              {/* RELATÓRIOS E ALERTAS */}
              <Route path="/relatorio" element={<RelatorioPage />} />
              <Route path="/exportacoes" element={<ExportacoesPage />} />
              <Route path="/alertas" element={<AlertasPage />} />
              <Route path="/divergencias" element={<DivergenciasPage />} />
              <Route path="/importacoes" element={<ImportacoesPage />} />
              <Route path="/ajuda" element={<AjudaPage />} />

              {/* ADMINISTRAÇÃO UNIFICADA */}
              <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminPage /></ProtectedRoute>} />
              <Route path="/parametros" element={<Navigate to="/admin" replace />} />
              <Route path="/regras" element={<Navigate to="/admin" replace />} />
              <Route path="/usuarios" element={<Navigate to="/admin" replace />} />
              <Route path="/admin/assinaturas" element={<Navigate to="/admin" replace />} />
              <Route path="/minha-assinatura" element={<MinhaAssinaturaPage />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
          <CookieConsentBanner />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
    </LanguageProvider>
  </QueryClientProvider>
  </AppErrorBoundary>
);

export default App;
